/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  Dispatch,
} from 'react';
import { useToast } from '@facephi/ui-react';
import axios, { AxiosResponse } from 'axios';
import jwt, { JwtPayload } from 'jsonwebtoken';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { RequestMethods, useLogger } from '../hooks';
import { useVariables } from '../hooks/useVariables';
import {
  AuthName,
  RoutesName,
  endPoints,
  imageFace,
  userAvatar,
  vocalTemplate,
  termsAcceptedState,
  TermsText,
  TermsAcceptance,
} from '../states';
import {
  LoginDto,
  TokenGrantType,
  ClaimsType,
  CLAIMS_TOKEN,
  ClaimsList,
} from '../states/model/auth';

const VOID_FUNCTION = () => undefined;

type IProps = {
  children: React.ReactNode;
};

type ContextProps = {
  logged?: boolean;
  logout(): void;
  terms?: boolean;
  setTerms: Dispatch<boolean>;
  handleLogin(user: LoginDto): void;
  acceptTerms(): void;
  handleRefreshToken(): Promise<boolean>;
  error: string | null;
  token: string | null;
  tokenValid: boolean;
  tenants: string[];
  email?: string;
  processStarted: boolean;
  setProcessStarted: Dispatch<boolean>;
  tenantDefault?: string;
  demoVersion: string;
  loadingTerms: boolean;
  termsText?: string;
};

const Ctx = createContext<ContextProps>({
  logged: false,
  logout: () => {},
  terms: false,
  acceptTerms: () => {},
  handleLogin: () => {},
  handleRefreshToken: () => new Promise((resolve) => resolve(true)),
  error: null,
  token: null,
  tokenValid: false,
  tenants: [],
  processStarted: false,
  setProcessStarted: VOID_FUNCTION,
  setTerms: VOID_FUNCTION,
  demoVersion: '',
  loadingTerms: true,
  termsText: '',
});

export const AuthProvider = ({ children }: IProps) => {
  const { apiDemoUrl, appApiId, appTesting } = useVariables();

  const [tenants, setTenants] = useState<string[]>([]);
  const [email, setEmail] = useState<string>();

  const [terms, setTerms] = useRecoilState(termsAcceptedState);

  const [logged, setLogged] = useState<boolean>(
    localStorage.getItem(AuthName.token) ? true : false
  );

  const [token, setToken] = useState<string | null>(
    localStorage.getItem(AuthName.token)
  );
  const [refreshToken, setRefreshToken] = useState<string | null>(
    localStorage.getItem(AuthName.refreshToken)
  );

  const [error, setError] = useState<string | null>(null);

  const [processStarted, setProcessStarted] = useState<boolean>(false);
  const [tenantDefault, setTenantDefault] = useState<string>();

  const { captureException, setUser: setUserSentry } = useLogger();
  const [demoVersion, setDemoVersion] = useState<string>('');
  const [loadingTerms, setLoadingTerms] = useState<boolean>(true);
  const { toastManager } = useToast();
  const { t } = useTranslation();
  const [termsText, setTermsText] = useState<string>();

  const isLogged = (token: string, refreshToken: string) => {
    localStorage.setItem(AuthName.token, token);
    localStorage.setItem(AuthName.refreshToken, refreshToken);
    setLogged(true);
  };

  const logout = () => {
    localStorage.removeItem(AuthName.token);
    localStorage.removeItem(AuthName.refreshToken);
    setToken(null);
    setRefreshToken(null);
    setEmail('');
    setTermsText('');
    localStorage.removeItem(imageFace);
    localStorage.removeItem(userAvatar);
    localStorage.removeItem(vocalTemplate);
    setLogged(false);
    setError(null);
    setTenantDefault(undefined);
    localStorage.removeItem('recoil-persist');
    window.location.replace(RoutesName.login);
    window.location.reload();
  };

  const checkTermsAccepted = () => {
    axios<TermsAcceptance>({
      method: RequestMethods.get,
      url: `${apiDemoUrl}${endPoints.TermsAcceptance}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AuthName.token)}`,
        'x-demo-app-version': demoVersion,
      },
      params: {
        userId: email,
      },
    })
      .then((response) => {
        const { userId } = response.data as any;
        if (userId && logged) {
          setTerms(true);
        }
      })
      .catch(() => {
        setTerms(false);
      })
      .finally(() => setLoadingTerms(false));
  };

  const acceptTerms = () => {
    setLoadingTerms(true);
    axios({
      method: RequestMethods.post,
      url: `${apiDemoUrl}${endPoints.TermsAcceptance}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem(AuthName.token)}`,
        'x-demo-app-version': '1',
      },
      data: {
        userId: email,
      },
    })
      .then((response) => {
        const { userId } = response.data as any;
        setTerms(true);
        toastManager({
          duration: 3500,
          type: 'success',
          message: `${t('T&C accepted successfully')}: ${userId}`,
          testId: 'toast-accepted-ok',
        });
      })
      .catch((error) => {
        captureException(error as Error, { operation: 'acceptingTAC' });
        toastManager({
          duration: 3500,
          type: 'error',
          message: t('Something went wrong with the acceptance of T&C'),
          testId: 'toast-accepted-ko',
        });
      })
      .finally(() => setLoadingTerms(false));
  };

  const tokenValid = useMemo(() => {
    if (token) {
      const decodeToken: JwtPayload | null = jwt.decode(token, {
        complete: true,
      });
      const dateNow = new Date();
      return decodeToken?.payload?.exp * 1000 > dateNow.getTime();
    }

    return false;
  }, [token]);

  const getClaims = (tokenInfo: JwtPayload) =>
    Object.entries(tokenInfo).reduce((acc, [key, value]) => {
      if (key.includes(CLAIMS_TOKEN)) {
        acc[key.replace(CLAIMS_TOKEN, '')] = value;
      }
      return acc;
    }, {} as ClaimsType);

  useEffect(() => {
    if (token) {
      const tokenInfo: JwtPayload = jwt.decode(token) as JwtPayload;
      setEmail(tokenInfo['http://facephi.com/identity/claims/email']);
      setTenants(tokenInfo['http://facephi.com/identity/claims/tenant']);
      const claims = getClaims(tokenInfo);
      if (claims.tenant) {
        if (claims[ClaimsList.tenant] instanceof Array) {
          // TO DO setTenantDefault(claims[ClaimsList.tenant][0] as string);
          setTenantDefault('8d04089d-8273-442e-ad40-2bf10ff494b3');
        }
      }
      setUserSentry({
        email: tokenInfo['http://facephi.com/identity/claims/email'],
      });
    }
  }, [token]);

  useEffect(() => {
    if (!demoVersion && logged) {
      const latest = '1';
      setDemoVersion(latest);
    }
  }, [logged]);

  const handleLogin = async (user: LoginDto) => {
    try {
      const response: AxiosResponse<
        { access_token: string; refresh_token: string },
        string
      > = await axios.post(`${apiDemoUrl}/auth/token`, {
        application_api_id: appApiId,
        grant_type: TokenGrantType.PasswordRealm,
        ...user,
      });

      if (response) {
        const {
          data: { access_token, refresh_token },
        } = response;
        setToken(access_token);
        setRefreshToken(refresh_token);
        isLogged(access_token, refresh_token);
      }
    } catch (error) {
      captureException(error as Error, { operation: 'handle Login' });
      if (axios.isAxiosError(error)) setError(error.response?.data.message);
    }
  };

  useEffect(() => {
    if (token) {
      axios<TermsText>({
        method: RequestMethods.get,
        url: `${apiDemoUrl}${endPoints.Terms}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem(AuthName.token)}`,
          'x-demo-app-version': '1',
        },
      })
        .then((response) => {
          const { content } = response.data as any;
          setTermsText(content);
        })
        .catch((error) => {
          captureException(error as Error, { operation: 'fetchingTAC' });
          if (appTesting) {
            toastManager({
              duration: 3500,
              type: 'error',
              canClose: true,
              message: t('Error while trying to retrieve TAC'),
              testId: 'toast-get-accepted-ko',
            });
          }
        });
    }
  }, [token]);

  useEffect(() => {
    if (termsText && email) {
      checkTermsAccepted();
    }
  }, [termsText, email]);

  const handleRefreshToken = (): Promise<boolean> =>
    new Promise((resolve, reject) => {
      axios
        .post(`${apiDemoUrl}/auth/token`, {
          application_api_id: appApiId,
          grant_type: TokenGrantType.RefreshToken,
          refresh_token: refreshToken,
        })
        .then((response) => {
          setToken(response.data.access_token);
          setRefreshToken(response.data.refresh_token);

          isLogged(response.data.access_token, response.data.refresh_token);
          resolve(true);
        })
        .catch(() => reject(false));
    });

  return (
    <Ctx.Provider
      value={{
        logged,
        logout,
        handleLogin,
        terms,
        loadingTerms,
        acceptTerms,
        error,
        handleRefreshToken,
        token,
        tokenValid,
        tenants,
        email,
        processStarted,
        setProcessStarted,
        setTerms,
        tenantDefault,
        demoVersion,
        termsText,
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useAuth = () => useContext(Ctx);
